import { useEffect, useMemo, useState } from 'react'
import { Check, CreditCard } from 'lucide-react'
import { changePlan, getPlan } from '../../api/settings'
import Button from '../../components/Button'
import { IN_APP } from '../../inApp'
import ErrorBanner from '../../components/ErrorBanner'
import HelpTip from '../../components/HelpTip'
import Modal from '../../components/Modal'
import { StatusBadge } from '../../components/PageParts'
import Spinner from '../../components/Spinner'
import { Card } from '../../components/Summary'
import { useToast } from '../../components/Toast.jsx'
import { fmtDay } from '../../utils/format'
import PlanCards, { LineSwitch } from '../../components/PlanCards'
import { SettingsHeader, SettingsTabs } from './settingsShared'

const money = (n) => `$${Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}`

/** A whole number from min to 100 with − and + buttons. */
function Stepper({ id, value, min = 0, onChange, label }) {
  const set = (n) => onChange(Math.max(min, Math.min(100, Number.isFinite(n) ? n : min)))
  return (
    <div className="plan-stepper">
      <button type="button" onClick={() => set(value - 1)} disabled={value <= min} aria-label={`Fewer ${label}`}>−</button>
      <input id={id} inputMode="numeric" value={value} onChange={(e) => set(parseInt(e.target.value.replace(/\D/g, '') || '0', 10))} />
      <button type="button" onClick={() => set(value + 1)} aria-label={`More ${label}`}>+</button>
    </div>
  )
}

// Plan & billing: the company's plan, add-ons, users and locations, and what
// it costs each month. Changes apply at once; ASYS invoices from the log.
export default function PlanSettings() {
  const { notify } = useToast()
  const [data, setData] = useState(null)
  const [error, setError] = useState('')
  const [plan, setPlan] = useState('')
  const [line, setLine] = useState('invoice')
  const [addons, setAddons] = useState([])
  const [extraUsers, setExtraUsers] = useState(0)
  const [extraLocations, setExtraLocations] = useState(0)
  const [saving, setSaving] = useState(false)
  const [lost, setLost] = useState(null)

  const load = () => getPlan().then((d) => {
    setData(d)
    const acct = d.account
    const p = d.plans.find((x) => x.id === acct.plan)
    // On a legacy plan (Full) the company keeps it until it picks another.
    setPlan(p ? p.id : (d.current ? acct.plan : ''))
    if (p) setLine(p.line)
    setAddons(p ? acct.modules.filter((m) => !p.modules.includes(m)) : [])
    setExtraUsers(acct.extra_users || 0)
    setExtraLocations(acct.extra_locations || 0)
  })
  useEffect(() => { load().catch((e) => setError(e.message || 'Couldn’t load your plan.')) }, [])

  const acct = data?.account
  const chosen = data?.plans.find((p) => p.id === plan) || (plan && plan === data?.account.plan ? data.current : undefined)
  const monthly = useMemo(() => (chosen
    ? chosen.price + chosen.addons.filter((a) => addons.includes(a.key)).reduce((t, a) => t + a.price, 0)
      + extraUsers * data.extra_user + extraLocations * data.extra_location
    : null), [chosen, addons, extraUsers, extraLocations, data])
  // A legacy plan (Full) isn't in the list but can be kept as it is.
  const legacy = acct && !data.plans.some((p) => p.id === acct.plan) && acct.plan !== 'custom'
  const usersIn = chosen?.users ?? acct?.user_limit ?? 0
  const locationsIn = chosen?.locations ?? acct?.location_limit ?? 1
  const minUsers = Math.max(0, (acct?.users || 0) - usersIn)
  const minLocations = Math.max(0, (acct?.locations || 0) - locationsIn)
  const currentPrice = acct?.price ?? null
  const changed = acct && chosen && (plan !== acct.plan || extraUsers !== acct.extra_users || extraLocations !== acct.extra_locations
    || [...addons].sort().join() !== acct.modules.filter((m) => !chosen.modules.includes(m)).sort().join())

  const pickPlan = (id) => {
    setPlan(id)
    const p = data.plans.find((x) => x.id === id)
    // Keep add-ons the new plan offers too, and enough extras for who's already here.
    setAddons((list) => list.filter((k) => p.addons.some((a) => a.key === k)))
    setExtraUsers((n) => Math.max(n, (acct?.users || 0) - p.users))
    setExtraLocations((n) => Math.max(n, (acct?.locations || 0) - p.locations))
  }
  const cardAction = (p) => {
    if (p.id === acct.plan) return { label: 'Your current plan', disabled: true }
    if (p.id === plan) return { label: 'Selected', onClick: () => {} }
    const up = currentPrice == null || p.price > currentPrice
    return { label: up ? 'Upgrade' : 'Switch to this plan', onClick: () => pickPlan(p.id), primary: up }
  }
  const toggle = (key) => setAddons((list) => (list.includes(key) ? list.filter((k) => k !== key) : [...list, key]))

  const save = async (confirm = false) => {
    setSaving(true)
    setError('')
    try {
      await changePlan({ plan, addons, extra_users: extraUsers, extra_locations: extraLocations, confirm })
      setLost(null)
      notify('Plan changed')
      // The whole app follows the plan: reload so every page sees it.
      setTimeout(() => window.location.reload(), 600)
    } catch (e) {
      if (e.status === 409) setLost(e.data?.lost || [])
      else setError(e.message || 'Couldn’t change the plan.')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="ci-page">
      <SettingsTabs />
      <SettingsHeader title="Plan & billing" meta={IN_APP ? 'Your plan, what it includes and what it costs each month.' : 'Your plan, what it includes and what it costs each month. Changes apply straight away.'} />
      {error && <ErrorBanner message={error} />}
      {!data ? <Spinner label="Loading your plan…" /> : (
        <>
          <div className="sum-tiles">
            <div className="plan-now">
              <span>Your plan</span>
              <strong>{acct.label}</strong>
              <em>{acct.monthly != null ? `${money(acct.monthly)}/month` : 'Priced by ASYS'}</em>
            </div>
            <div className="plan-now">
              <span>Users</span>
              <strong>{acct.users} of {acct.user_limit ?? 'any'}</strong>
              <em>{acct.cashiers ? `plus ${acct.cashiers} cashier${acct.cashiers === 1 ? '' : 's'} (free)` : 'Cashiers are free'}</em>
            </div>
            <div className="plan-now">
              <span>Locations</span>
              <strong>{acct.locations} of {acct.location_limit ?? 'any'}</strong>
              <em>{money(data.extra_location)} for each extra</em>
            </div>
            {acct.trial_days_left != null && (
              <div className="plan-now">
                <span>Free trial</span>
                <strong>{acct.trial_days_left < 0 ? 'Ended' : `${acct.trial_days_left} days left`}</strong>
                <em>{acct.trial_ends_at ? `Until ${fmtDay(acct.trial_ends_at)}` : ''}</em>
              </div>
            )}
          </div>

          {IN_APP ? (
            <p className="doc-hint"><CreditCard size={13} aria-hidden="true" /> Your plan can’t be changed in the app. To change it, contact ASYS.</p>
          ) : (<>
          <section className="plan-cards" aria-labelledby="plan-cards-title">
            <h2 id="plan-cards-title">Change your plan <HelpTip text="Upgrades and add-ons switch on at once. A change that turns something off asks first; nothing you’ve recorded is deleted, and it comes back if you switch it on again." /></h2>
            {legacy && (
              <p className="mf-notice">
                <StatusBadge label={acct.label} tone="info" />
                Your company has every part of ASYS on a plan we no longer offer. You can stay on it; choose a plan below only if you want to change.
              </p>
            )}
            <div className="plan-switch-row"><LineSwitch lines={data.lines} value={line} onChange={setLine} /></div>
            <PlanCards data={data} line={line} action={cardAction} selected={plan !== acct.plan ? plan : undefined} />
          </section>

          <Card title={chosen ? `${chosen.label}: add-ons, users and locations` : 'Add-ons, users and locations'}>
            {chosen && chosen.addons.length > 0 && (
              <div className="plan-section">
                <h3>Add-ons</h3>
                {chosen.addons.map((a) => (
                  <label key={a.key} className="plan-addon">
                    <input type="checkbox" checked={addons.includes(a.key)} onChange={() => toggle(a.key)} />
                    <span>{a.label}</span><em>+{money(a.price)}/month</em>
                  </label>
                ))}
              </div>
            )}

            <div className="plan-section plan-extras">
              <div className="plan-extra">
                <label htmlFor="plan-extra-users">Extra users<span>{usersIn} included; cashiers are free. +{money(data.extra_user)}/month each.</span></label>
                <Stepper id="plan-extra-users" label="users" value={extraUsers} min={minUsers} onChange={setExtraUsers} />
              </div>
              <div className="plan-extra">
                <label htmlFor="plan-extra-locations">Extra locations<span>{locationsIn} included. +{money(data.extra_location)}/month each.</span></label>
                <Stepper id="plan-extra-locations" label="locations" value={extraLocations} min={minLocations} onChange={setExtraLocations} />
              </div>
            </div>

            <div className="plan-total">
              <div>
                <strong>{monthly != null ? `${money(monthly)}/month` : ''}</strong>
                <span>{changed && acct.monthly != null && monthly != null
                  ? `${monthly >= acct.monthly ? '+' : '−'}${money(Math.abs(monthly - acct.monthly))} a month from now on`
                  : `${usersIn + extraUsers} users (cashiers free) and ${locationsIn + extraLocations} location${locationsIn + extraLocations === 1 ? '' : 's'}`}</span>
              </div>
              <Button onClick={() => save(false)} loading={saving} disabled={!changed}><Check size={14} />Change plan</Button>
            </div>
          </Card>
          </>)}

          {data.history.length > 0 && (
            <Card title="Changes">
              <ul className="sum-list">
                {data.history.map((h, i) => (
                  <li key={i}>
                    <span className="sum-list-main">
                      {(data.plans.find((p) => p.id === h.to_plan) || {}).label || (h.to_plan === 'full' ? 'Full (legacy)' : h.to_plan)}
                      <span className="cell-sub">{[fmtDay(h.changed_at), h.changed_by].filter(Boolean).join(' · ')}</span>
                    </span>
                    <span className="sum-list-side">{h.monthly_after != null ? `${money(h.monthly_after)}/month` : ''}</span>
                  </li>
                ))}
              </ul>
            </Card>
          )}
          <p className="doc-hint"><CreditCard size={13} aria-hidden="true" /> ASYS sends a monthly invoice for your plan, add-ons and extras. Questions about billing? Contact ASYS.</p>
        </>
      )}

      <Modal
        open={!!lost}
        onClose={() => setLost(null)}
        title="Turn these off?"
        subtitle={`${(lost || []).join(', ')} will be hidden for everyone in your company. Nothing is deleted: switch ${(lost || []).length === 1 ? 'it' : 'them'} back on any time and everything is there.`}
        footer={<>
          <Button variant="ghost" onClick={() => setLost(null)}>Keep my plan</Button>
          <Button variant="danger" onClick={() => save(true)} loading={saving}>Change plan</Button>
        </>}
      />
    </div>
  )
}
